import * as React from 'react';
import { useState, useCallback, useMemo } from 'react';
import RaterTable from './RaterTable';
import ProjectLayout from '../../ProjectLayout';
import SurveyHeader from '../SurveyHeader';
import { css } from '@emotion/core';
import { FLEX_ROW } from '../../../layout/flexBox';
import LocalBtn from '../../../localization/LocalBtn';
import { Str } from '../../../localization/translationKeys';
import { btnStyle } from '../../../forms/styles/button';
import { COLORS } from '../../../globalStyle/colors';
import { useHistory } from 'react-router-dom';
import { useRoutes } from '../../../navigation/routes';
import { useDispatch } from 'react-redux';
import { HIDE_ON_MOBILE, onMobileDevice, SHOW_ON_MOBILE } from '../../../globalStyle/mediaQueries';
import { useAppState } from '../../../core/redux/hooks';
import { participantRatersAction } from './participantRatersAction';
import { useLoading, useError, useErrorOfType } from '../../../core/api/hooks';
import { ActionType } from '../../../core/redux/actionType';
import { useMsgText } from '../../../localization/hooks';
import { sendInvitationsAction } from './sendInvitationsAction';
import { Loader } from '../../../loading/Loader';
import { useWindowSize } from '../../../core/hooks/useWindowSize';
import MobileRaterActionsBar from './MobileRaterActionsBar';
import { previousRatersAction } from './previousRatersAction';
import { useToggle } from '../../../core/hooks/useToggle';
import { sendForApprovalAction } from './sendForApprovalAction';
import LeaveInviteModal from './LeaveInviteModal';
import { isValidDate, diffS, TIME } from '../../../language/time';
import RequirementBar from './RequirementBar';
import RequirementMobileMenu from './RequirementMobileMenu';
import LocalDiv from '../../../localization/LocalDiv';
import { inviteEmailPreviewAction } from './inviteEmailPreviewAction';
import CustomizeEmailModal from './CustomizeEmailModal';
import { approverEmailPreviewAction } from './approverEmailPreviewAction';
import { isNilOrEmpty } from '../../../language/module';
import { useToggleOnSuccess } from '../../../core/hooks/useToggleOnSuccess';
import { addRaterByIdAction } from './addRaterByIdAction';
import { undeliveredEmailsAction } from './undeliveredEmailsAction';
import { useProjIds } from '../../../navigation/hooks';
import { withValidMinOrMax, withValidMinimum } from './module';
import { Rater } from './models';
import NavigationPrompt from 'react-router-navigation-prompt';
import { ProjStr } from '../../messages/ProjStr';
import ProjMsg from '../../messages/ProjMsg';
import { useOnSuccess } from '../../../core/hooks/useOnSuccess';
import { RaterApprovalOption } from '../../../raterApproval/raterApprovalOption';
import { useRaterActions, useRaterData } from './hooks';
import { useInterval } from '../../../core/hooks/useInterval';
import { logInfoAction } from '../../../traceLogging/api/logInfoAction';
import { clearLoadingStateAction } from '../../../core/redux/clearLoadingStateAction';


const styles = {
  header:    css`
    margin-bottom: 40px;
    ${onMobileDevice} {
      margin-bottom: 35px;
    }
  `,
  content:   css`
  `,
  breakLine: css`
    height: 1px;
    background-color: ${COLORS.LIGHT_GREY};
    border: 0 none;
  `,
  introBar:  {
    root:      css`
      justify-content: space-between;
      align-items: center;
      margin: 16px;
    `,
    separator: css`
      margin: auto;
      flex-basis: 25%;
    `,
    remindBtn: css`
      flex-basis: 132px;
      flex-shrink: 0;
    `,
    introMsg:  css`
    a {
      // Like PLAIN_GREEN_LINKS, but not bolded
      color: #73C724;
      text-decoration: none;
    }
  `,
  },
  loader:    {
    root:    css`
      margin-top: 240px;
      justify-content: center;
      align-items: center;
      ${onMobileDevice} {
        margin-top: 120px;
      }
    `,
    spinner: css`
      width: 60px;
    `,
  },
};


type Props = {
  className?: string;
};


/**
 * InviteRaters: a component that renders a table showing the raters currently associated to the current project, as
 * well as several UI elements which allow manipulation of the rater set (adding, removing, requesting approval for
 * raters, etc.).
 *
 * TODO: this component is too large -- it should be broken out
 *
 * PureComponent: true
 * Redux Connected: false
 */
const InviteRaters: React.FC<Props> = props => {
  const history  = useHistory();
  const dispatch = useDispatch();

  const { isMobile } = useWindowSize();

  const projIds = useProjIds();
  const project = useAppState(state => state.projectSummary);

  // Local data
  const [email, setEmail]                     = React.useState('');
  const [inviteComment, setInviteComment]     = React.useState<string>('');
  const [approverComment, setApproverComment] = React.useState<string>('');

  // Modals/toggled components
  const [showingApproverEmailModal, toggleApproverEmailModal] = useToggle();
  const [showingInviteModal, toggleInviteModal]               = useToggle();
  const [showingMobileRequirements, toggleMobileRequirements] = useToggle();

  // Messaging
  const pageTitle = useMsgText(Str.InviteRemindRaters);

  const [approverEmailSent] = useToggleOnSuccess(ActionType.SendForApprovalRequest, `${projIds.projId}`);
  const [invitationsSent]   = useToggleOnSuccess(ActionType.SendInvitationsRequest, `${projIds.projId}`);

  // Edit rater data state
  // This lives here so we can track the rater Id and proj Id to trigger error messaging
  const [underEdit, setUnderEdit]     = useState<Rater | undefined>(undefined);
  const [underUpdate, setUnderUpdate] = useState<Rater | undefined>(undefined);

  const markRaterForEdit = useCallback(
    (rater: Rater | undefined) => {
      setUnderEdit(rater);

      // Don't un-mark the rater as under update until the API request returns with success
      if (rater === undefined) {
        return;
      }

      setUnderUpdate(rater);
    },
    [],
  );

  const cancelRaterEdit = useCallback(
    () => {
      setUnderEdit(undefined);
      setUnderUpdate(undefined);
    },
    [],
  );

  const updatingRater      = useLoading(ActionType.UpdateRaterRequest, `${projIds.projId}-${underUpdate?.raterId}`);
  const errorUpdatingRater = !!useError(ActionType.UpdateRaterRequest, `${projIds.projId}-${underUpdate?.raterId}`);
  const desyncedRaterId    = useMemo(
    () => errorUpdatingRater ? underUpdate?.raterId : undefined,
    [errorUpdatingRater, underUpdate],
  );

  useOnSuccess(
    () => setUnderUpdate(undefined),
    ActionType.UpdateRaterRequest,
    `${projIds.projId}-${underUpdate?.raterId}`,
  );

  const requirements    = useAppState(state => state.participantRequirements);
  const languages       = useAppState(state => state.projectLanguages?.[`${projIds.projId}`] ?? []);
  const currentLanguage = useAppState(state => state.locale.lang);
  
  const { previousRaters, raters, raterLevels } = useRaterData(projIds.projId);

  React.useEffect(
    () => {
      if (projIds.participantId < 0 || projIds.projId < 0) {
        return;
      }

      if (!raters) {
        dispatch(participantRatersAction(projIds.projId, projIds.participantId));
      }

      if (!previousRaters) {
        dispatch(previousRatersAction(projIds.projId, projIds.participantId));
      }
    },
    [dispatch, previousRaters, projIds.participantId, projIds.projId, raters],
  );

  React.useEffect(
    () => {
      if ((!currentLanguage) || projIds.projId < 0) {
        return;
      }

      dispatch(inviteEmailPreviewAction(projIds.projId, currentLanguage.languageId));
    },
    [currentLanguage, dispatch, projIds.projId],
  );

  const undeliveredEmails = useAppState(state => state.undeliveredEmails);

  React.useEffect(
    () => {
      // For reasons currently unclear, if the undelivered email address list is returned before the rater set,
      // undelivered raters are never properly marked as such. Hence, until a more durable fix can be delivered, we
      // wait for raters to be returned.
      if (!raters) {
        return;
      }

      if (projIds.projId < 0 || projIds.participantId < 0) {
        return;
      }

      if (!undeliveredEmails) {
        dispatch(undeliveredEmailsAction(projIds.projId, projIds.participantId));
      }
    },
    [dispatch, projIds.participantId, projIds.projId, raters, undeliveredEmails],
  );

  const inviteBody = useAppState(
    state => state.inviteEmailTemplates[`${(projIds.projId)}`]?.[`${currentLanguage.languageId}`] || '',
  );

  const approverEmailBody = useAppState(
    state => state.approverEmailTemplates[`${(projIds.projId)}`]?.[`${currentLanguage.languageId}`] || '',
  );

  const routes = useRoutes(projIds.participantId, projIds.projDisplayId);

  const approval = !!project?.inviteRatersRaterApprovalOption;

  const raterArr = useMemo(() => Object.values(raters ?? {}), [raters]);

  const { notInvited, invited, notApproved, approvers } = React.useMemo(
    () => ({
      notInvited:  raterArr
                     .filter(rater => !isValidDate(rater.invitedDate))
                     .sort((rater1, rater2) => rater2.raterId - rater1.raterId),
      invited:     raterArr
                     .filter(rater => isValidDate(rater.invitedDate))
                     .sort((rater1, rater2) => rater2.raterId - rater1.raterId),
      notApproved: raterArr.filter(rater => rater.approvalStatus === 0 || rater.approvalStatus === 2),
      approvers:   raterArr.filter(rater => rater.approvalStatus === 1),
    }),
    [raterArr],
  );

  //region Approver-selected admin handling

  const approverIsAdminSelected = React.useMemo(
    () => project?.inviteRatersRaterApprovalOption === RaterApprovalOption.SelectedByAdmin,
    [project],
  );

  const approverSelectedAdminEmail = React.useMemo(() => project?.inviteRatersApproverEmail, [project]);

  //endregion Approver-selected admin handling

  const approverEmail = React.useMemo(() => approvers.length > 0 ? approvers[0].email : '', [approvers]);
  React.useEffect(
    () => {
      if (projIds.projId < 0) {
        return;
      }

      if (!currentLanguage) {
        return;
      }

      dispatch(approverEmailPreviewAction(projIds.projId, currentLanguage.languageId, approverEmail));
    },
    [dispatch, projIds.projId, currentLanguage.languageId, approverEmail, currentLanguage],
  );

  // Used for `sendTo` parameter when requesting approval for raters
  const approverAddresses = React.useMemo(
    () => approverIsAdminSelected
      ? [{ toAddress: approverSelectedAdminEmail ?? '', languageId: currentLanguage.languageId }]
      : approvers.map(r => ({ toAddress: r.email, languageId: r.languageId })),
    [approverIsAdminSelected, approverSelectedAdminEmail, approvers, currentLanguage.languageId],
  );
  // ... and when inviting raters...
  const addressesToInvite = React.useMemo(
    () => notInvited.map(r => ({ toAddress: r.email, languageId: r.languageId })),
    [notInvited],
  );

  const directorySearchEnabled = React.useMemo(
    () => project?.inviteRatersRaterSelectionOption === 2 
      || project?.inviteRatersRaterSelectionOption === 3,
    [project],
  );

  const manualEntryEnabled = React.useMemo(
    () => project?.inviteRatersRaterSelectionOption === 1 
      || project?.inviteRatersRaterSelectionOption === 3 
      || project?.inviteRatersRaterSelectionOption === 4,
    [project],
  );

  const searchDirectory = React.useCallback(
    () => history.push(routes.SearchDirectory),
    [routes.SearchDirectory, history],
  );

  const nonAssociatedPreviousRaterIds = React.useMemo(
    () => !!previousRaters && Object.keys(previousRaters).filter(r => !raters?.[r]),
    [previousRaters, raters],
  );

  const nonAssociatedPreviousRaters = React.useMemo(
    () => !!nonAssociatedPreviousRaterIds && nonAssociatedPreviousRaterIds.map(raterId => previousRaters?.[raterId]),
    [nonAssociatedPreviousRaterIds, previousRaters],
  );

  const addPreviousRaters = React.useCallback(
    () => Object
      .values(nonAssociatedPreviousRaters)
      .map(rater => dispatch(addRaterByIdAction(
        projIds.projId,
        projIds.participantId,
        rater.raterId,
        rater.surveyLevelId,
      ))),
    [nonAssociatedPreviousRaters, projIds.participantId, projIds.projId, dispatch],
  );

  const { addRater, deleteRater, setApprover, updateRater } = useRaterActions(
    projIds.projId,
    projIds.participantId,
    dispatch,
  );

  const sendInvitations = React.useCallback(
    () => dispatch(sendInvitationsAction(projIds.projId, projIds.participantId, inviteComment, addressesToInvite)),
    [addressesToInvite, inviteComment, projIds.participantId, projIds.projId, dispatch],
  );

  const sendForApproval = React.useCallback(
    () => dispatch(sendForApprovalAction(projIds.projId, projIds.participantId, approverComment, approverAddresses)),
    [approverAddresses, approverComment, projIds.participantId, projIds.projId, dispatch],
  );

  const loadingRaters      = useLoading(ActionType.ParticipantRatersRequest, `${projIds.projId}`);
  const addingRater        = useLoading(ActionType.AddRaterByEmailRequest, `${email}-${projIds.projId}`);
  const sendingInvitations = useLoading(ActionType.SendInvitationsRequest, `${projIds.projId}`);
  const sendingForApproval = useLoading(ActionType.SendForApprovalRequest, `${projIds.projId}`);

  const isLoading = loadingRaters || addingRater || updatingRater || sendingInvitations || sendingForApproval;

  const errorAddingRater         = !!useError(ActionType.AddRaterByEmailRequest, `${email}-${projIds.projId}`);
  const addPreviousRaterErrors   = useErrorOfType(ActionType.AddRaterByIdRequest);
  const errorAddingPreviousRater = React.useMemo(() => !isNilOrEmpty(addPreviousRaterErrors), [addPreviousRaterErrors]);

  const showPreviousRatersBtn = React.useMemo(
    () => !!project?.inviteRatersAutoPopulateRaters && !isNilOrEmpty(previousRaters),
    [previousRaters, project],
  );

  const disableCustomizeEmailBtn = React.useMemo(
    () => project?.surveyPastDue || !approverEmailBody,
    [approverEmailBody, project],
  );

  const disableCustomizeInviteBtn = React.useMemo(
    () => project?.surveyPastDue || !inviteBody,
    [inviteBody, project],
  );

  const nonEmptyRequirements = React.useMemo(
    () => !!project?.maximumsRequired ? withValidMinOrMax(requirements) : withValidMinimum(requirements),
    [project, requirements],
  );

  const { disableApproveBtn, disableInviteBtn } = React.useMemo(
    () => {
      const minReq         = !!project?.minimumsRequired;
      const raterArr       = Object.values(raters || {}) as Rater[];
      const needMoreRaters = minReq && requirements!.filter(r => r.raterCount < r.minimumRequired).length > 0;

      const approvalReady = raterArr.filter(rater => rater.approvalStatus === 0 || rater.approvalStatus === 2);

      const uninvitedRaters = raterArr.filter(rater => !isValidDate(rater.invitedDate));

      return {
        disableInviteBtn:  !uninvitedRaters.length || needMoreRaters || !!project?.surveyPastDue || sendingInvitations,
        disableApproveBtn: !approvalReady.length || needMoreRaters || !!project?.surveyPastDue,
      };
    },
    [project, raters, requirements, sendingInvitations],
  );

  const user = useAppState(state => state.auth.currentUser);
  const code = useAppState(state => state.locale.lang.code);

  const dateDue = React.useMemo(
    () => project && project.surveyDeadline
      ? (new Date(project.surveyDeadline)).toLocaleDateString(code)
      : '',
    [project, code],
  );
  const pastDue = useMemo(() => project?.surveyPastDue, [project]);

  const remindRaters = () => {
    history.push(routes.RemindRaters);
  };

  // Insert the self-survey pass key into the passkey generator link
  const introTagMapping = React.useMemo(() => ({ 'cRaterPW': project?.selfSurveyPasskey }), [project]);

  //region sendInvitationsAction Debug Fix

  const lastInviteRequestStamp = useAppState(state => state.lastInviteRequest);

  const logSlowInviteRequest = useCallback(
    () => dispatch(logInfoAction(
      'Send rater invites request has exceeded 30 seconds',
      ActionType.SendInvitationsRequest,
    )),
    [dispatch],
  );

  const clearLoadingState = useCallback(
    () => { dispatch(clearLoadingStateAction(ActionType.SendInvitationsRequest, `${projIds.projId}`)); },
    [dispatch, projIds.projId],
  );

  useInterval(
    () => {
      if (!sendingInvitations) {
        return;
      }

      if (!lastInviteRequestStamp) {
        return;
      }

      const delta = diffS(lastInviteRequestStamp ?? new Date(), new Date());
      if (sendingInvitations && !!lastInviteRequestStamp && Math.abs(delta) >= 30) {
        logSlowInviteRequest();
        clearLoadingState();
        sendInvitations();
      }
    },
    15 * TIME.S,
  );

  //endregion

  return (
    <ProjectLayout className={props.className}>
      <SurveyHeader title={`/ ${pageTitle}`} deadline={project?.surveyDeadline} css={styles.header} />
      <LocalDiv css={styles.content}>
        <div css={[FLEX_ROW, styles.introBar.root]}>
          <ProjMsg
            str={ProjStr.InviteRatersIntro}
            css={styles.introBar.introMsg}
            pointyTokens={introTagMapping}
          />
          <div css={styles.introBar.separator} />
          { !pastDue && (
              <LocalBtn
                str={Str.RemindRaters}
                css={[HIDE_ON_MOBILE, btnStyle.transparent(COLORS.DARK_GREY), styles.introBar.remindBtn]}
                disabled={!project?.raterInvited || project.surveyPastDue}
                onClick={() => remindRaters()}
              />
          )}
        </div>
        {
          !isNilOrEmpty(nonEmptyRequirements) && (
            <>
              <hr css={[styles.breakLine, HIDE_ON_MOBILE]} />
              <RequirementMobileMenu
                maximumsRequired={!!project?.maximumsRequired}
                minimumsRequired={!!project?.minimumsRequired}
                isOpen={showingMobileRequirements}
                toggleOpen={toggleMobileRequirements}
                requirements={nonEmptyRequirements!}
                css={SHOW_ON_MOBILE}
              />
              <RequirementBar
                maximumsRequired={!!project?.maximumsRequired}
                minimumsRequired={!!project?.minimumsRequired}
                requirements={nonEmptyRequirements!}
                css={HIDE_ON_MOBILE}
              />
            </>
          )
        }
        <RaterTable
          routes={routes}
          code={code}
          raters={notInvited.concat(invited)}
          raterLevels={raterLevels || []}
          projectLanguages={languages || []}
          requirements={requirements || []}
          undeliveredEmails={undeliveredEmails ?? []}
          raterUnderEdit={underEdit}
          markRaterForEdit={markRaterForEdit}
          cancelRaterEdit={cancelRaterEdit}
          desyncedRaterId={desyncedRaterId}
          updatingRater={updatingRater}
          addingRater={addingRater}
          errorAddingRater={errorAddingRater}
          errorAddingPreviousRater={errorAddingPreviousRater}
          sendingInvitations={sendingInvitations}
          invitationsSent={invitationsSent}
          sendingForApproval={sendingForApproval}
          approverSent={approverEmailSent}
          approval={approval}
          approverIsAdminSelected={approverIsAdminSelected}
          adminSelectedApproverName={project?.inviteRatersApproverName}
          approvalLevelId={project?.inviteRatersApproverLevelId ?? 0}
          approverEmailSent={project?.approverEmailSent || false}
          showNameFields={!!project?.inviteRatersPromptRaterNames}
          requireNamesOnAdd={!!project?.inviteRatersRequireRaterNames}
          minimumsRequired={!!project?.minimumsRequired}
          maximumsRequired={!!project?.maximumsRequired}
          enableAddPreviousRaters={showPreviousRatersBtn}
          enableDirectorySearch={directorySearchEnabled}
          enableManualEntry={manualEntryEnabled}
          surveyPastDue={!!project?.surveyPastDue}
          disableCustomizeEmailButton={disableCustomizeEmailBtn}
          disableCustomizeInviteButton={disableCustomizeInviteBtn}
          disableAddPreviousRatersButton={isNilOrEmpty(nonAssociatedPreviousRaterIds)}
          setEmail={setEmail}
          toggleCustomizeInviteModal={toggleInviteModal}
          toggleCustomizeApproverEmailModal={toggleApproverEmailModal}
          toggleMobileRequirements={toggleMobileRequirements}
          addPreviousRaters={addPreviousRaters}
          addRater={addRater}
          updateRater={updateRater}
          deleteRater={deleteRater}
          sendInvitations={sendInvitations}
          setApprover={setApprover}
          sendForApproval={sendForApproval}
        />
        <div css={[FLEX_ROW, styles.loader.root]}>
          {isLoading && <Loader css={styles.loader.spinner} />}
        </div>
        {
          isMobile && !pastDue &&  (
            <MobileRaterActionsBar
              showPreviousRatersButton={showPreviousRatersBtn}
              showDirectorySearchButton={!showPreviousRatersBtn && directorySearchEnabled}
              disableAddPreviousRatersButton={isNilOrEmpty(nonAssociatedPreviousRaterIds)}
              disableSearchDirectoryButton={!directorySearchEnabled}
              disableCustomizeModalButton={approval ? disableCustomizeEmailBtn : disableCustomizeInviteBtn}
              disableRemindRatersButton={!!project?.surveyPastDue}
              disableInviteOrApproveButton={approval ? disableApproveBtn : disableInviteBtn}
              customizeModalButtonLabel={approval ? Str.CustomizeEmail : Str.CustomizeInvite}
              sendMsgButtonLabel={approval ? Str.SendForApproval : Str.InviteRaters}
              onClickSearchDirectoryButton={searchDirectory}
              onClickAddPreviousRatersButton={addPreviousRaters}
              onClickSendRemindersButton={remindRaters}
              onClickCustomizeModalButton={approval ? toggleApproverEmailModal : toggleInviteModal}
              onClickSendMsgButton={approval ? sendForApproval : sendInvitations}
            />
          )
        }
        {
          showingInviteModal && (
            <CustomizeEmailModal
              dismiss={toggleInviteModal}
              modalTitle={Str.CustomizeInvite}
              emailBody={inviteBody}
              emailComment={inviteComment}
              setEmailComment={setInviteComment}
              ieIframeRoute={routes.InviteEmailPreview}
              emailLogo={project?.clientImageUrl ?? ''}
              participant={user?.personName ?? ''}
              survey={project?.surveyName ?? ''}
              dateDue={dateDue}
              ratersApproved={''}
              ratersDeclined={''}
              ratersAdded={''}
            />
          )
        }
        {
          showingApproverEmailModal && (
            <CustomizeEmailModal
              dismiss={toggleApproverEmailModal}
              modalTitle={Str.CustomizeEmail}
              emailBody={approverEmailBody}
              emailComment={approverComment}
              setEmailComment={setApproverComment}
              ieIframeRoute={routes.ApproverEmailPreview}
              emailLogo={project?.clientImageUrl ?? ''}
              participant={user?.personName ?? ''}
              survey={project?.surveyName ?? ''}
              dateDue={dateDue}
              ratersApproved={''}
              ratersDeclined={''}
              ratersAdded={''}
            />
          )
        }
        <NavigationPrompt
          when={
            (currentLocation, nextLocation) =>
              !nextLocation?.pathname?.includes('inviteRaters')
              && !nextLocation?.pathname?.toLowerCase().includes('modal')
              && !nextLocation?.pathname?.toLowerCase().includes('searchdirectory')
              && (approval ? notApproved.length : notInvited.length) > 0
          }
        >
          {
            ({ onCancel, onConfirm }) => (
              <LeaveInviteModal
                approval={approval}
                unhandledRaters={approval ? notApproved.length : notInvited.length}
                onClickYes={onConfirm}
                onDismiss={onCancel}
              />
            )
          }
        </NavigationPrompt>
      </LocalDiv>
    </ProjectLayout>
  );
};

export default InviteRaters;
